import React, { useEffect, useState } from 'react';
import ClassicEditor from '@ckeditor/ckeditor5-build-classic';
import { CKEditor } from '@ckeditor/ckeditor5-react';
import { useDispatch, useSelector } from 'react-redux';
import toast from 'react-hot-toast';
import { fetchPolicy, savePolicy } from '../../Redux/Reducers/PolicyReducer';
import { Box, Typography, Button, Paper, Grid } from '@mui/material';
import { useTranslation } from 'react-i18next';

const TermsAndCondition = () => {
    const dispatch = useDispatch();
    const { t } = useTranslation();
    const { policies, loading } = useSelector((state) => state.PolicyReducer);

    const [dataEn, setDataEn] = useState("");
    const [dataFr, setDataFr] = useState("");

    const type = "terms-condition";

    /* ================= FETCH ================= */
    useEffect(() => {
        dispatch(fetchPolicy(type));
    }, [dispatch]);

    useEffect(() => {
        if (policies[type]) {
            setDataEn(policies[type]?.en?.content || "");
            setDataFr(policies[type]?.fr?.content || "");
        }
    }, [policies]);

    /* ================= SAVE ================= */
    const handleSave = async () => {
        try {
            await dispatch(
                savePolicy({
                    type,
                    en: {
                        title: "Terms & Conditions",
                        content: dataEn
                    },
                    fr: {
                        title: "Conditions Générales",
                        content: dataFr
                    }
                })
            ).unwrap();

            toast.success(
                t('terms_conditions_saved', 'Terms & Conditions saved successfully!')
            );
        } catch (err) {
            toast.error(
                t('terms_conditions_save_failed', 'Failed to save terms & conditions')
            );
        }
    };

    return (
        <Box sx={{ p: '36px 24px 24px' }}>
            {/* ===== HEADER ===== */}
            <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: '24px', flexWrap: 'wrap', columnGap: '6px', rowGap: '16px' }}>
                <Typography variant="h5" sx={{ fontWeight: 700, color: 'white', lineHeight: '32px' }} className='main_title'>{t('terms_conditions', 'Terms & Conditions')}</Typography>
                <Button
                    variant="contained"
                    onClick={handleSave}
                    disabled={loading}
                    sx={{ 
                        border: '1px solid rgba(230, 210, 249, 0.1) !important',
                        color: '#fff',
                        textTransform: 'none',
                        backgroundColor: '#FF69B4 !important',
                        borderRadius: '999px',
                        fontSize: '15px',
                        lineHeight: '24px',
                        padding: '6px 20px',
                        minWidth: '38px',
                        '& .MuiButton-startIcon': {
                        margin: 0,
                        },
                    }}
                >
                    {loading ? t('saving', 'Saving...') : t('save_changes', 'Save Changes')}
                </Button>
            </Box>

            {/* ===== EDITORS ===== */}
            <Grid container spacing={3}>
                {/* ===== ENGLISH ===== */}
                <Grid item xs={12} md={6}>
                    <Typography variant="h6" sx={{ mb: 1, color: 'white', fontSize: '18px', fontWeight: 500 }}>{t('english', 'English')}</Typography>
                    <Paper 
                        sx={{
                            borderRadius: 2,
                            overflow: "hidden",
                            border: "none !important",
                            backgroundColor: "#252525 !important",
                            borderRadius: '20px !important',
                            boxShadow: 'none !important',
                            mb: 2,
                            "& .MuiPaper-root": {
                                borderRadius: '0 !important',
                            },
                            "& .ck.ck-editor": {
                                backgroundColor: "#252525",
                            },
                            "& .ck.ck-toolbar": {
                                backgroundColor: "#252525 !important",
                                borderColor: "rgba(230, 210, 249, 0.2) !important",
                                borderRadius: "20px 20px 0 0 !important"
                            },
                            "& .ck.ck-toolbar *": {
                                color: "#ffffff !important",
                            },
                            "& .ck.ck-editor__editable": {
                                backgroundColor: "#252525 !important",
                                color: "#ffffff !important",
                                minHeight: "300px",
                                borderColor: "rgba(230, 210, 249, 0.2) !important",
                                boxShadow: "none !important",
                                borderRadius: '0 0 20px 20px !important',
                                fontSize: "14px !important"
                            },
                            "& .ck.ck-editor__main .ck-editor__editable:not(.ck-focused)": {
                                borderColor: "rgba(230, 210, 249, 0.2) !important",
                            },
                            "& .ck.ck-editor__editable::placeholder": {
                                color: "rgba(255,255,255,0.5)",
                            },
                            "& .ck.ck-editor__editable.ck-focused": {
                                borderColor: "rgba(230, 210, 249, 0.2) !important",
                                boxShadow: "0 0 0 1px rgba(230, 210, 249, 0.2) inset",
                            },
                            "& .ck.ck-button:hover": {
                                backgroundColor: "rgba(255,255,255,0.08) !important",
                            },
                            "& .ck.ck-button.ck-on": {
                                backgroundColor: "rgba(230, 210, 249, 0.2) !important",
                            },
                            "& .ck.ck-dropdown__panel": {
                                backgroundColor: "#1a1a1a !important",
                                border: "1px solid rgba(230, 210, 249, 0.2)",
                                boxShadow: "0 8px 20px rgba(0,0,0,0.6)",
                            },
                            "& .ck.ck-list": {
                                backgroundColor: "#1a1a1a !important",
                            },
                            "& .ck.ck-list__item .ck-button": {
                                color: "#ffffff !important",
                            },
                            "& .ck.ck-list__item .ck-button:hover": {
                                backgroundColor: "rgba(255,255,255,0.08) !important",
                            },
                            "& .ck.ck-list__item .ck-button.ck-on": {
                                backgroundColor: "rgba(230, 210, 249, 0.2) !important",
                                color: "#000 !important",
                            },
                            "& .ck.ck-balloon-panel": {
                                backgroundColor: "#1a1a1a !important",
                                border: "1px solid rgba(230, 210, 249, 0.2)",
                                boxShadow: "0 8px 20px rgba(0,0,0,0.6)",
                            },
                            "& .ck.ck-input-text": {
                                backgroundColor: "#121212 !important",
                                color: "#ffffff !important",
                                border: "1px solid rgba(230, 210, 249, 0.2)",
                            },
                            "& .ck.ck-input-text:focus": {
                                borderColor: "rgba(230, 210, 249, 0.2) !important",
                                boxShadow: "none",
                            },
                        }}
                    >
                        <CKEditor
                            editor={ClassicEditor}
                            data={dataEn}
                            config={{
                                toolbar: [
                                    "heading", "|",
                                    "bold", "italic", "link",
                                    "bulletedList", "numberedList", "|",
                                    "blockQuote", "insertTable"
                                ],
                                placeholder: t(
                                    'enter_terms_conditions_en',
                                    'Enter Terms & Conditions in English...'
                                )
                            }}
                            onChange={(event, editor) =>
                                setDataEn(editor.getData())
                            }
                        />
                    </Paper>
                </Grid>

                {/* ===== FRENCH ===== */}
                <Grid item xs={12} md={6}>
                    <Typography variant="h6" sx={{ mb: 1, color: 'white', fontSize: '18px', fontWeight: 500 }}>{t('french', 'French')}</Typography>
                    <Paper
                        sx={{
                            borderRadius: 2,
                            overflow: "hidden",
                            border: "none !important",
                            backgroundColor: "#252525 !important",
                            borderRadius: '20px !important',
                            boxShadow: 'none !important',
                            mb: 2,
                            "& .MuiPaper-root": {
                                borderRadius: '0 !important',
                            },
                            "& .ck.ck-editor": {
                                backgroundColor: "#252525",
                            },
                            "& .ck.ck-toolbar": {
                                backgroundColor: "#252525 !important",
                                borderColor: "rgba(230, 210, 249, 0.2) !important",
                                borderRadius: "20px 20px 0 0 !important"
                            },
                            "& .ck.ck-toolbar *": {
                                color: "#ffffff !important",
                            },
                            "& .ck.ck-editor__editable": {
                                backgroundColor: "#252525 !important",
                                color: "#ffffff !important",
                                minHeight: "300px",
                                borderColor: "rgba(230, 210, 249, 0.2) !important",
                                boxShadow: "none !important",
                                borderRadius: '0 0 20px 20px !important',
                                fontSize: "14px !important"
                            },
                            "& .ck.ck-editor__main .ck-editor__editable:not(.ck-focused)": {
                                borderColor: "rgba(230, 210, 249, 0.2) !important",
                            },
                            "& .ck.ck-editor__editable::placeholder": {
                                color: "rgba(255,255,255,0.5)",
                            },
                            "& .ck.ck-editor__editable.ck-focused": {
                                borderColor: "rgba(230, 210, 249, 0.2) !important",
                                boxShadow: "0 0 0 1px rgba(230, 210, 249, 0.2) inset",
                            },
                            "& .ck.ck-button:hover": {
                                backgroundColor: "rgba(255,255,255,0.08) !important",
                            },
                            "& .ck.ck-button.ck-on": {
                                backgroundColor: "rgba(230, 210, 249, 0.2) !important",
                            },
                            "& .ck.ck-dropdown__panel": {
                                backgroundColor: "#1a1a1a !important",
                                border: "1px solid rgba(230, 210, 249, 0.2)",
                                boxShadow: "0 8px 20px rgba(0,0,0,0.6)",
                            },
                            "& .ck.ck-list": {
                                backgroundColor: "#1a1a1a !important",
                            },
                            "& .ck.ck-list__item .ck-button": {
                                color: "#ffffff !important",
                            },
                            "& .ck.ck-list__item .ck-button:hover": {
                                backgroundColor: "rgba(255,255,255,0.08) !important",
                            },
                            "& .ck.ck-list__item .ck-button.ck-on": {
                                backgroundColor: "rgba(230, 210, 249, 0.2) !important",
                                color: "#000 !important",
                            },
                            "& .ck.ck-balloon-panel": {
                                backgroundColor: "#1a1a1a !important",
                                border: "1px solid rgba(230, 210, 249, 0.2)",
                                boxShadow: "0 8px 20px rgba(0,0,0,0.6)",
                            },
                            "& .ck.ck-input-text": {
                                backgroundColor: "#121212 !important",
                                color: "#ffffff !important",
                                border: "1px solid rgba(230, 210, 249, 0.2)",
                            },
                            "& .ck.ck-input-text:focus": {
                                borderColor: "rgba(230, 210, 249, 0.2) !important",
                                boxShadow: "none",
                            },
                        }}
                    >
                        <CKEditor
                            editor={ClassicEditor}
                            data={dataFr}
                            config={{
                                toolbar: [
                                    "heading", "|",
                                    "bold", "italic", "link",
                                    "bulletedList", "numberedList", "|",
                                    "blockQuote", "insertTable"
                                ],
                                placeholder: t(
                                    'enter_terms_conditions_fr',
                                    'Entrez les conditions générales en français...'
                                )
                            }}
                            onChange={(event, editor) =>
                                setDataFr(editor.getData())
                            }
                        />
                    </Paper>
                </Grid>
            </Grid>
        </Box>
    );
};

export default TermsAndCondition;
